import { useCallback } from 'react'
import type { Node, Edge, ReactFlowInstance } from '@xyflow/react'
import type { NodeCategory } from '@bandos/shared'
import type { ReactFlowNodeData } from '../../../../types/mindmap-components'
import { generateNodeId, createNodeData, createEdge, NODE_DEFAULTS } from '../../../../utils'
import { ROOT_X, ROOT_Y, calculateParentPosition } from '../../utils/nodePositioning'
import { smartPlaceChild } from '../../utils/smartLayout'

// ============================================================================
// TYPES
// ============================================================================

/** Dependencies required by the useNodeCreation hook */
export interface UseNodeCreationDeps {
  /** Current nodes array */
  nodes: Node<ReactFlowNodeData>[]
  /** Current edges array */
  edges: Edge[]
  /** Setter for nodes state */
  setNodes: React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>
  /** Setter for edges state */
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  /** Current mindmap ID */
  mindmapId: string | undefined
  /** Callback to set auto-edit node ID (focuses the new node for editing) */
  setAutoEditNodeId: (nodeId: string | undefined) => void
  /** Ref to ReactFlow instance for fitView operations */
  reactFlowInstanceRef: React.RefObject<ReactFlowInstance<Node<ReactFlowNodeData>, Edge> | null>
}

/** Return type of the useNodeCreation hook */
export interface UseNodeCreationReturn {
  /**
   * Creates a new child node connected to a parent node.
   * Positions the child node offset to the right and down from the parent.
   * Automatically creates an edge connecting parent to child.
   * Auto-focuses the new node for immediate editing.
   */
  executeChildNodeCreation: (parentId: string, selectedCategory: NodeCategory) => string | undefined
  /**
   * Creates a new root node (not connected to any parent).
   * If position is provided, uses that position. Otherwise uses a default position.
   * Auto-focuses the new node for immediate editing.
   */
  executeRootNodeCreation: (
    selectedCategory: NodeCategory,
    position?: { x: number; y: number }
  ) => void
  /**
   * Creates a new node when user drags an edge and drops it on the canvas.
   * Uses the exact drop position for the new node.
   * Creates an edge connecting the source node to the new node.
   * Auto-focuses the new node for immediate editing.
   */
  executeEdgeDropNodeCreation: (
    selectedCategory: NodeCategory,
    position: { x: number; y: number },
    sourceNodeId: string
  ) => void
  /**
   * Creates a new parent node connected to an existing child node.
   * Positions the parent node offset to the left of the child.
   * Edge direction: parent (source) -> child (target).
   * This is the reverse of child creation.
   * Generic - can be used by any node type to add a parent.
   */
  executeParentNodeCreation: (
    childId: string,
    parentCategory: NodeCategory,
    parentData?: Partial<ReactFlowNodeData>
  ) => string | undefined
  /**
   * Creates a new parent node at a specific drop position (from edge drag).
   * Edge direction: parent (source) -> child (target).
   * Auto-focuses the new node for immediate editing.
   */
  executeParentDropNodeCreation: (
    parentCategory: NodeCategory,
    position: { x: number; y: number },
    childNodeId: string
  ) => void
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Custom hook that encapsulates all node creation logic for the mindmap.
 *
 * Handles three types of node creation:
 * 1. Child nodes - connected to a parent node
 * 2. Root nodes - standalone nodes not connected to any parent
 * 3. Edge drop nodes - created when dragging an edge to empty canvas
 *
 * @example
 * ```tsx
 * const { executeChildNodeCreation, executeRootNodeCreation, executeEdgeDropNodeCreation } = useNodeCreation({
 *   nodes,
 *   edges,
 *   setNodes,
 *   setEdges,
 *   mindmapId,
 *   setAutoEditNodeId,
 *   reactFlowInstanceRef
 * })
 *
 * // Create a child node
 * executeChildNodeCreation(parentNodeId, 'idea')
 *
 * // Create a root node
 * executeRootNodeCreation('venture')
 *
 * // Create a node from edge drop
 * executeEdgeDropNodeCreation('job', { x: 500, y: 300 }, sourceNodeId)
 * ```
 */
export function useNodeCreation({
  nodes,
  edges,
  setNodes,
  setEdges,
  mindmapId,
  setAutoEditNodeId,
  reactFlowInstanceRef,
}: UseNodeCreationDeps): UseNodeCreationReturn {
  /**
   * Creates a new ReactFlow node with the given parameters.
   */
  const createNewNode = useCallback(
    (
      nodeId: string,
      category: NodeCategory,
      position: { x: number; y: number }
    ): Node<ReactFlowNodeData> => {
      const nodeData = createNodeData({
        nodeId,
        category,
        mindmapId,
      })

      return {
        id: nodeId,
        position,
        data: nodeData,
        type: `${category}Node`,
      }
    },
    [mindmapId]
  )

  /**
   * Creates a child node connected to a parent.
   * Positions child to the right of parent, stacking vertically if siblings exist.
   */
  const executeChildNodeCreation = useCallback(
    (parentId: string, selectedCategory: NodeCategory): string | undefined => {
      // Use getNodes() to get current positions (avoids stale closure after node movement)
      const currentNodes = reactFlowInstanceRef.current?.getNodes() ?? nodes
      const currentEdges = reactFlowInstanceRef.current?.getEdges() ?? edges

      const parentNode = currentNodes.find((n) => n.id === parentId)
      if (!parentNode) return undefined

      const newNodeId = generateNodeId()
      const childPosition = smartPlaceChild(parentNode, currentNodes, currentEdges)

      const newNode = createNewNode(newNodeId, selectedCategory, childPosition)
      // Explicitly specify source handle to ensure edge comes from right side (not left-source used for parent connections)
      const newEdge = createEdge(parentId, newNodeId, currentNodes, currentEdges, {
        sourceHandle: 'right-source',
        targetHandle: 'target',
      })

      setNodes((prevNodes) => [...prevNodes, newNode])
      setEdges((prevEdges) => [...prevEdges, newEdge])

      setAutoEditNodeId(newNodeId)
      return newNodeId
    },
    [nodes, edges, setNodes, setEdges, setAutoEditNodeId, createNewNode, reactFlowInstanceRef]
  )

  /**
   * Creates a root node (not connected to any parent).
   * If position is provided, uses that position. Otherwise uses default position.
   */
  const executeRootNodeCreation = useCallback(
    (selectedCategory: NodeCategory, position?: { x: number; y: number }) => {
      const newNodeId = generateNodeId()

      // Use provided position or fall back to default
      const rootPosition = position ?? { x: ROOT_X, y: ROOT_Y }

      const newNode = createNewNode(newNodeId, selectedCategory, rootPosition)

      setNodes((currentNodes) => [...currentNodes, newNode])
      setAutoEditNodeId(newNodeId)
    },
    [setNodes, setAutoEditNodeId, createNewNode]
  )

  /**
   * Creates a node from an edge drop (dragging edge to empty canvas).
   */
  const executeEdgeDropNodeCreation = useCallback(
    (selectedCategory: NodeCategory, position: { x: number; y: number }, sourceNodeId: string) => {
      const newNodeId = generateNodeId()
      const newNode = createNewNode(newNodeId, selectedCategory, position)
      // Explicitly specify source handle to ensure edge comes from right side
      const newEdge = createEdge(sourceNodeId, newNodeId, nodes, edges, {
        sourceHandle: 'right-source',
        targetHandle: 'target',
      })

      setNodes((currentNodes) => [...currentNodes, newNode])
      setEdges((currentEdges) => [...currentEdges, newEdge])

      setAutoEditNodeId(newNodeId)
    },
    [nodes, edges, setNodes, setEdges, setAutoEditNodeId, createNewNode]
  )

  /**
   * Creates a parent node connected to an existing child node.
   * Positions parent to the left of child at the same Y.
   * Edge direction: parent (source) -> child (target).
   * Returns the new node ID if successful, undefined otherwise.
   */
  const executeParentNodeCreation = useCallback(
    (
      childId: string,
      parentCategory: NodeCategory,
      parentData?: Partial<ReactFlowNodeData>
    ): string | undefined => {
      // Use getNodes() to get current positions (avoids stale closure after node movement)
      const currentNodes = reactFlowInstanceRef.current?.getNodes() ?? nodes
      const currentEdges = reactFlowInstanceRef.current?.getEdges() ?? edges

      const childNode = currentNodes.find((n) => n.id === childId)
      if (!childNode) return undefined

      const newNodeId = generateNodeId()
      const parentPosition = calculateParentPosition(childNode)

      const newNode = createNewNode(newNodeId, parentCategory, parentPosition)

      // Merge any additional data (e.g., company-specific fields)
      if (parentData) {
        newNode.data = { ...newNode.data, ...parentData }
      }

      // Edge direction: parent (source, right side) -> child (target, left side)
      const newEdge = createEdge(newNodeId, childId, currentNodes, currentEdges, {
        sourceHandle: 'right-source',
        targetHandle: 'target',
      })

      setNodes((prevNodes) => [...prevNodes, newNode])
      setEdges((prevEdges) => [...prevEdges, newEdge])

      return newNodeId
    },
    [nodes, edges, setNodes, setEdges, createNewNode, reactFlowInstanceRef]
  )

  /**
   * Creates a parent node at a specific drop position (from edge drag).
   * Edge direction: parent (source) -> child (target).
   * Position is adjusted so the node's right edge aligns with the drop point.
   */
  const executeParentDropNodeCreation = useCallback(
    (parentCategory: NodeCategory, position: { x: number; y: number }, childNodeId: string) => {
      const currentNodes = reactFlowInstanceRef.current?.getNodes() ?? nodes
      const currentEdges = reactFlowInstanceRef.current?.getEdges() ?? edges

      const newNodeId = generateNodeId()

      // Offset position so the node's right edge aligns with the drop point
      const adjustedPosition = {
        x: position.x - NODE_DEFAULTS.width,
        y: position.y,
      }
      const newNode = createNewNode(newNodeId, parentCategory, adjustedPosition)

      // Edge direction: parent (source, right side) -> child (target, left side)
      const newEdge = createEdge(newNodeId, childNodeId, currentNodes, currentEdges, {
        sourceHandle: 'right-source',
        targetHandle: 'target',
      })

      setNodes((currentNodes) => [...currentNodes, newNode])
      setEdges((currentEdges) => [...currentEdges, newEdge])

      setAutoEditNodeId(newNodeId)
    },
    [nodes, edges, setNodes, setEdges, setAutoEditNodeId, createNewNode, reactFlowInstanceRef]
  )

  return {
    executeChildNodeCreation,
    executeRootNodeCreation,
    executeEdgeDropNodeCreation,
    executeParentNodeCreation,
    executeParentDropNodeCreation,
  }
}
